import React, { useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormFeedback } from 'reactstrap';
import { SWIFT_BIC_REGEX, validateIban } from '../../../_helper/regexHandler';
import { useModalCrudState } from '../../../_helper/hooks/useModalCrudState';

const BankDataModal = ({ apiEndpoint, isOpen, toggle, mode, selectedBank, refreshData }) => {
    const initialBankData = {
        id: '',
        bankName: '',
        bankCode: '',
        branchName: '',
        branchCode: '',
        iban: '',
        swiftBicCode: '',
        currency: '',
    };

    const validationSchema = useMemo(() => ({
        bankName: { required: true, message: 'Banka Adı zorunludur.' },
        branchName: { required: true, message: 'Şube Adı zorunludur.' },
        iban: {
            required: true,
            message: 'IBAN zorunludur.',
            validator: validateIban,
            validatorMessage: 'Geçerli bir IBAN formatı giriniz.',
        },
        currency: { required: true, message: 'Para birimi zorunludur.' },
        swiftBicCode: {
            regex: SWIFT_BIC_REGEX,
            regexMessage: 'Geçerli bir SWIFT/BIC kodu giriniz (örn: AKBKTRISXXX)',
        },
    }), []);

    const {
        formData: bankData,
        errors,
        handleCancel,
        handleInputChange,
        handleSubmit,
        isSubmitting,
    } = useModalCrudState({
        isOpen,
        mode,
        selectedItem: selectedBank,
        initialData: initialBankData,
        toggle,
        mapSelectedToData: ({ initialData, selectedItem }) => ({
            ...initialData,
            ...selectedItem,
        }),
        normalizeInput: true,
        validationSchema,
        submitConfig: {
            apiEndpoint,
            refreshData,
            componentName: 'Banka',
        },
    });

    const getModalTitle = () => {
        return mode === 'add' ? 'Banka Ekle' : 'Banka Düzenle';
    };

    const getButtonLabel = () => {
        return mode === 'add' ? 'Ekle' : 'Güncelle';
    };

    return (
        <Modal isOpen={isOpen} toggle={handleCancel}>
            <ModalHeader toggle={handleCancel}>{getModalTitle()}</ModalHeader>
            <ModalBody>
                <Form>
                    <FormGroup>
                        <Label for="bankName">Banka Adı <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="bankName"
                            name="bankName"
                            value={bankData.bankName}
                            onChange={handleInputChange}
                            placeholder="Banka Adı"
                            invalid={!!errors.bankName}
                        />
                        <FormFeedback>
                            {errors.bankName}
                        </FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="bankCode">Banka Kodu</Label>
                        <Input
                            type="text"
                            id="bankCode"
                            name="bankCode"
                            value={bankData.bankCode}
                            onChange={handleInputChange}
                            invalid={!!errors.bankCode}
                        />
                        <FormFeedback>
                            {errors.bankCode}
                        </FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="branchName">Şube Adı <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="branchName"
                            name="branchName"
                            value={bankData.branchName}
                            onChange={handleInputChange}
                            placeholder="Şube Adı"
                            invalid={!!errors.branchName}
                        />
                        <FormFeedback>
                            {errors.branchName}
                        </FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="branchCode">Şube Kodu</Label>
                        <Input
                            type="text"
                            id="branchCode"
                            name="branchCode"
                            value={bankData.branchCode}
                            onChange={handleInputChange}
                            invalid={!!errors.branchCode}
                        />
                        <FormFeedback>
                            {errors.branchCode}
                        </FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="iban">IBAN <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="iban"
                            name="iban"
                            value={bankData.iban}
                            onChange={handleInputChange}
                            placeholder="TR00 0000 0000 0000 0000 0000 00"
                            invalid={!!errors.iban}
                        />
                        <FormFeedback>
                            {errors.iban}
                        </FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="currency">Para Birimi <span className="text-danger">*</span></Label>
                        <Input
                            type="select"
                            id="currency"
                            name="currency"
                            value={bankData.currency}
                            onChange={handleInputChange}
                            invalid={!!errors.currency}
                        >
                            <option value="">Seçiniz</option>
                            <option value="USD">USD (Amerikan Doları)</option>
                            <option value="EUR">EUR (Euro)</option>
                            <option value="TRY">TRY (Türk Lirası)</option>
                            <option value="GBP">GBP (İngiliz Sterlini)</option>
                        </Input>
                        <FormFeedback>
                            {errors.currency}
                        </FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="swiftBicCode">SWIFT/BIC Kodu</Label>
                        <Input
                            type="text"
                            id="swiftBicCode"
                            name="swiftBicCode"
                            value={bankData.swiftBicCode}
                            onChange={handleInputChange}
                            placeholder="AKBKTRISXXX"
                            invalid={!!errors.swiftBicCode}
                        />
                        <FormFeedback>
                            {errors.swiftBicCode}
                        </FormFeedback>
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <button className="btn btn-outline-success rounded-pill" onClick={handleSubmit} disabled={isSubmitting}>
                    {isSubmitting ? 'Kaydediliyor...' : getButtonLabel()}
                </button>
                <button className="btn btn-outline-danger rounded-pill" onClick={handleCancel}>
                    İptal
                </button>
            </ModalFooter>
        </Modal>
    );
};

export default BankDataModal;
